import { Code, ConnectError } from '@connectrpc/connect';
import { GpRegion } from '~/plugins/grpc/universal/types';
import { USER_CURRENCY_STORE_KEY } from '~/store/currency';
import { convertGpRegionToLegacy } from '~/utils/legacyRegion';

export function useRegionSwitch() {
  const authStore = useAuthStore();
  const webinterfaceRegionCookie = useCookie('webinterface_region');

  const isSwitching = ref(false);

  function hasCustomerInRegion(region: GpRegion): boolean {
    return authStore.customers[region] != null;
  }

  /**
   * Ensures a customer exists in the target region, creating one if needed.
   * Treats AlreadyExists as success (race condition / stale local state).
   */
  async function ensureCustomerInRegion(region: GpRegion): Promise<boolean> {
    if (hasCustomerInRegion(region)) return true;
    try {
      await authStore.createCustomer(region);
      return true;
    } catch (error) {
      // Customer already exists on the backend but our local state was stale —
      // re-fetch customers so local state catches up, then treat as success.
      if (error instanceof ConnectError && error.code === Code.AlreadyExists) {
        try {
          await authStore.fetchCustomers();
        } catch {
          // fetchCustomers failed but customer exists — still proceed
        }
        return true;
      }
      return false;
    }
  }

  /**
   * Full region switch: ensures customer exists → changes preferred region →
   * updates cookie → syncs currency.
   *
   * Throws on failure — callers should wrap in try/catch.
   */
  async function switchRegion(targetRegion: GpRegion): Promise<void> {
    if (targetRegion === authStore.activeRegion) return;

    isSwitching.value = true;
    try {
      const customerReady = await ensureCustomerInRegion(targetRegion);
      if (!customerReady) {
        throw new Error('Failed to ensure customer in target region');
      }

      await authStore.changeUserPreferredRegion(targetRegion);

      webinterfaceRegionCookie.value = convertGpRegionToLegacy(targetRegion);

      // Clear any user-overridden currency so the new region's wallet currency takes effect.
      // Without this, useUserCurrency would restore the old currency from localStorage on next load.
      if (import.meta.client) {
        localStorage.removeItem(USER_CURRENCY_STORE_KEY);
      }
    } finally {
      isSwitching.value = false;
    }
  }

  return {
    isSwitching: readonly(isSwitching),
    hasCustomerInRegion,
    ensureCustomerInRegion,
    switchRegion,
  };
}
